/**
 * QuestionDetail — one questionnaire item, in the shared detail container.
 *
 * Replaces the global `questionnairePanel` side panel. Same endpoints, but
 * docked beside the list instead of sliding over it, so answering a run of
 * questions is now click-row → type → Finalize → click next row, rather than
 * open-panel → answer → close-panel, once per question.
 *
 * THREE STATES, one layout:
 *
 *   unanswered  the question, who it is directed to, an empty composer
 *   answered    the same, with the draft answer in the composer + Finalize
 *   finalized   the answer read-only, with Reopen (and Edit, which reopens
 *               the composer without changing the record)
 *
 * Endpoints, unchanged:
 *   create    `addQuestionnaireItem(matterUuid, question)`
 *   answer    `updateQuestionnaireItem(matterUuid, id, { ...item })`
 *   finalize  `finalizeQuestionnaireItem(matterUuid, id, answer?)`
 *   reopen    `restoreQuestionnaireItem(matterUuid, id)`
 *   to client `assignQuestionnaireItem(matterUuid, id)`
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCheckCircleLight, PiUserPlusLight } from 'react-icons/pi';
import { useAppDispatch } from '../../../../store';
import {
  addQuestionnaireItem,
  assignQuestionnaireItem,
  finalizeQuestionnaireItem,
  restoreQuestionnaireItem,
  updateQuestionnaireItem,
} from '../../../../store/actions/Matters/MatterActions';
import { showFeedbackModal } from '../../../../store/actions/UserFeedbackActions';
import Button from '../../../shared/TailwindComponents/Button';
import Textarea from '../../../shared/TailwindComponents/Textarea';
import Badge from '../../../shared/TailwindComponents/Badge';
import Toggle from '../../../shared/TailwindComponents/Toggle';
import { Well } from '../../../shared/brand/Field';
import Spinner from '../../../shared/TailwindComponents/Spinner';

/**
 * The id of the question `add_questionnaire_item` just created.
 *
 * The endpoint's response shape is not contractual — it has been seen wrapping
 * the record, returning it bare, and returning the whole refreshed list — so
 * every one of those is read, and the question text is the last resort. Returns
 * '' when none of them answer, which the caller treats as "created, not
 * assigned" rather than assigning the wrong row.
 */
export function createdQuestionId(response: any, questionText: string): string {
  const text = questionText.trim();
  const fromRecord = (record: any): string =>
    String(record?.question_id ?? record?.questionId ?? record?.id ?? '');

  const direct = fromRecord(response) || fromRecord(response?.data)
    || fromRecord(response?.question) || fromRecord(response?.data?.question);
  if (direct) return direct;

  const list = [response, response?.data, response?.questionnaire, response?.data?.questionnaire]
    .find((candidate) => Array.isArray(candidate)) as any[] | undefined;
  const match = list?.find((item) => String(item?.question || '').trim() === text);
  return match ? fromRecord(match) : '';
}

export interface DetailQuestion {
  question_id?: string;
  question: string;
  potential_answer?: string;
  who_answered?: string;
  when_answered?: string;
  status?: string;
  client_assigned?: boolean;
}

interface Props {
  matterUuid: string;
  /** Null in create mode. */
  question: DetailQuestion | null;
  canEdit: boolean;
  onSaved: () => void;
  onClose: () => void;
}

const QuestionDetail: React.FC<Props> = ({
  matterUuid,
  question,
  canEdit,
  onSaved,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const isCreate = !question;
  const isFinalized = question?.status === 'finalized';
  const questionId = question?.question_id || '';

  const [draftQuestion, setDraftQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState<'' | 'save' | 'finalize' | 'reopen' | 'assign'>('');
  const [reopenedForEdit, setReopenedForEdit] = useState(false);
  const [error, setError] = useState('');
  /** Create mode only: send the new question to the client as it is created. */
  const [assignOnCreate, setAssignOnCreate] = useState(false);

  /*
    Reset when the SELECTED question changes, keyed on its id.
    Keying on the object would reload the form on every background refresh —
    the poll that runs while an answer is being typed — and throw the draft
    away mid-sentence.
  */
  useEffect(() => {
    setDraftQuestion(question?.question || '');
    setAnswer(question?.potential_answer || '');
    setReopenedForEdit(false);
    setAssignOnCreate(false);
    setError('');
  }, [questionId, isCreate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fail = (message: string) => dispatch(showFeedbackModal({
    showModal: true, modalType: 'error', message, duration: 3000,
  }));

  const createQuestion = async () => {
    if (!draftQuestion.trim()) {
      setError(t('A question is required'));
      return;
    }
    setBusy('save');
    try {
      const text = draftQuestion.trim();
      const created: any = await dispatch(addQuestionnaireItem(matterUuid, text));

      /*
        ASSIGN IS A SECOND CALL, and it can only be made once the question has
        an id — `add_questionnaire_item` takes no assignment flag. When the
        response does not name the new row (see `createdQuestionId`) the
        question is still created; we say so plainly rather than silently
        dropping the toggle or assigning some other question.
      */
      if (assignOnCreate) {
        const newId = createdQuestionId(created, text);
        if (newId) {
          await dispatch(assignQuestionnaireItem(matterUuid, newId));
        } else {
          dispatch(showFeedbackModal({
            showModal: true,
            modalType: 'info',
            message: t('Question added'),
            subMsg: t('Open it in the list to send it to the client.'),
            duration: 5000,
          }));
        }
      }
      onSaved();
      onClose();
    } catch {
      fail(t('Failed to add question'));
    } finally {
      setBusy('');
    }
  };

  /** Save the draft answer without finalising — the question stays open. */
  const saveAnswer = async () => {
    if (!questionId) return;
    setBusy('save');
    try {
      await dispatch(updateQuestionnaireItem(matterUuid, questionId, {
        question: question!.question,
        potential_answer: answer,
        status: answer.trim() ? 'answered' : 'pending',
      }));
      onSaved();
    } catch {
      fail(t('Failed to save answer'));
    } finally {
      setBusy('');
    }
  };

  const finalize = async () => {
    if (!questionId) return;
    setBusy('finalize');
    try {
      // The answer rides along, so Finalize also commits an unsaved draft.
      await dispatch(finalizeQuestionnaireItem(matterUuid, questionId, answer));
      onSaved();
    } catch {
      fail(t('Failed to finalize question'));
    } finally {
      setBusy('');
    }
  };

  const reopen = async () => {
    if (!questionId) return;
    setBusy('reopen');
    try {
      await dispatch(restoreQuestionnaireItem(matterUuid, questionId));
      onSaved();
    } catch {
      fail(t('Failed to reopen question'));
    } finally {
      setBusy('');
    }
  };

  const assignToClient = async () => {
    if (!questionId) return;
    setBusy('assign');
    try {
      await dispatch(assignQuestionnaireItem(matterUuid, questionId));
      onSaved();
    } catch {
      fail(t('Failed to assign question'));
    } finally {
      setBusy('');
    }
  };

  /* ── Create ──────────────────────────────────────────────────── */

  if (isCreate) {
    return (
      <div className="p-4 space-y-4">
        <Textarea
          id="new-question"
          label={t('Question')}
          placeholder={t('What do you need to find out?')}
          value={draftQuestion}
          onChange={(e) => { setDraftQuestion(e.target.value); setError(''); }}
          error={error}
          rows={4}
          className="w-full"
        />

        {/* Who the question is for, decided WHERE it is written rather than
            as a follow-up action on the row afterwards. */}
        <Well>
          <Toggle
            enabled={assignOnCreate}
            onToggle={setAssignOnCreate}
            label={t('Assign to client')}
            description={t('The client is asked to answer this question in their portal.')}
            disabled={!!busy}
            size="small"
          />
        </Well>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-hairline-soft">
          <Button variant="neutral" size="small" onClick={onClose} disabled={!!busy}>
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={createQuestion}
            disabled={!!busy}
            icon={busy === 'save' ? <Spinner size="glyph" className="!h-4 !w-4" /> : undefined}
          >
            {t('Add question')}
          </Button>
        </div>
      </div>
    );
  }

  /* ── View / answer ───────────────────────────────────────────── */

  const showComposer = canEdit && (!isFinalized || reopenedForEdit);

  return (
    <div className="p-4 space-y-4">
      {/* The question, and who it is directed to. */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-fg-strong leading-snug">{question!.question}</p>
        <div className="flex items-center flex-wrap gap-2 text-[11px] text-fg-muted">
          <Badge
            color={isFinalized ? 'green' : question!.potential_answer ? 'blue' : 'yellow'}
            className="!text-[10px] !px-1.5 !py-0 capitalize"
          >
            {isFinalized ? t('Completed') : question!.potential_answer ? t('Answered') : t('Pending')}
          </Badge>
          <span>
            {t('Directed to')}:{' '}
            {question!.client_assigned ? t('Client') : t('Internal')}
          </span>
        </div>
      </div>

      {/* The answer: composer while open, read-only once finalized. */}
      {showComposer ? (
        <Textarea
          id={`answer-${questionId}`}
          label={t('Answer')}
          placeholder={t('Write the answer…')}
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          rows={8}
          expandable
          // An eight-row writing surface gets a writing surface's inset: the
          // first line had been sitting hard against the top edge of the box.
          className="w-full py-3"
        />
      ) : (
        <div className="space-y-1.5">
          <p className="text-[11px] font-medium text-fg-muted">{t('Answer')}</p>
          {question!.potential_answer ? (
            <Well>
              <p className="text-sm text-fg-body leading-relaxed whitespace-pre-wrap">
                {question!.potential_answer}
              </p>
            </Well>
          ) : (
            <p className="text-sm text-fg-muted">{t('No answer yet')}</p>
          )}
        </div>
      )}

      {(question!.who_answered || question!.when_answered) && (
        <p className="text-[10px] text-fg-muted">
          {question!.who_answered || t('You')}
          {question!.when_answered ? ` · ${question!.when_answered}` : ''}
        </p>
      )}

      {canEdit && (
        <div className="flex items-center justify-between gap-2 pt-3 border-t border-hairline-soft">
          {/* Sending to the client is only meaningful while it is still open. */}
          {!isFinalized && !question!.client_assigned ? (
            <Button
              variant="outline"
              size="small"
              onClick={assignToClient}
              disabled={!!busy}
              icon={<PiUserPlusLight className="h-3.5 w-3.5" />}
            >
              {t('Ask client')}
            </Button>
          ) : <span />}

          <div className="flex items-center gap-2">
            {isFinalized ? (
              <>
                {!reopenedForEdit && (
                  <Button variant="outline" size="small" onClick={() => setReopenedForEdit(true)}>
                    {t('Edit')}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="small"
                  onClick={reopen}
                  disabled={!!busy}
                  icon={busy === 'reopen' ? <Spinner size="glyph" className="!h-4 !w-4" /> : undefined}
                >
                  {t('Reopen')}
                </Button>
                {reopenedForEdit && (
                  <Button variant="primary" size="small" onClick={finalize} disabled={!!busy}>
                    {t('Save')}
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="small"
                  onClick={saveAnswer}
                  disabled={!!busy}
                  icon={busy === 'save' ? <Spinner size="glyph" className="!h-4 !w-4" /> : undefined}
                >
                  {t('Save')}
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  onClick={finalize}
                  disabled={!!busy || !answer.trim()}
                  tooltip={!answer.trim() ? t('Write an answer before finalizing.') : ''}
                  icon={busy === 'finalize'
                    ? <Spinner size="glyph" className="!h-4 !w-4" />
                    : <PiCheckCircleLight className="h-3.5 w-3.5" />}
                >
                  {t('Finalize')}
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionDetail;
